import React, { useState, useEffect } from 'react';
import '../styles/PlatformSelector.css';
import '../styles/SharedAnimations.css';

const PlatformSelector = ({ onArticleSelect }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(() => {
    const saved = localStorage.getItem('questionnaire_platform');
    return saved || '';
  });
  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem('questionnaire_step');
    return saved ? parseInt(saved) : 1;
  });
  const [same, setSame] = useState(() => {
    const saved = localStorage.getItem('questionnaire_same');
    return saved ? JSON.parse(saved) : null;
  });
  const [banned, setBanned] = useState(() => {
    const saved = localStorage.getItem('questionnaire_banned');
    return saved ? JSON.parse(saved) : null;
  });
  const [phone, setPhone] = useState(() => {
    const saved = localStorage.getItem('questionnaire_phone');
    return saved || null;
  });
  const [promotion, setPromotion] = useState(() => {
    const saved = localStorage.getItem('questionnaire_promotion');
    return saved || null;
  });
  const [method, setMethod] = useState(() => {
    const saved = localStorage.getItem('questionnaire_method');
    return saved || null;
  });
  const [articles, setArticles] = useState(() => {
    const saved = localStorage.getItem('questionnaire_articles');
    return saved ? JSON.parse(saved) : null;
  });

  const platformPaths = {
    'TikTok': '/articles/tiktok/usa-targeting.md',
    'Instagram': '/articles/instagram/usa-targeting.md',
    'Twitter': '/articles/twitter/usa-targeting.md',
    'Youtube Shorts': '/articles/youtube/usa-targeting.md',
    'Tinder and other dating apps': '/articles/tinder/usa-targeting.md',
    'Other': '/articles/targeting-issues/cheat-targeting.md'
  };

  const platforms = Object.keys(platformPaths);

  const getRecommendedMethod = (platform, isBanned) => {
    switch (platform) {
      case 'TikTok':
        return isBanned 
          ? 'SIM card manipulation 1 and 2, proxy via wifi or ethernet'
          : 'SIM CARD MANIPULATION 1 AND 2, Local 4G from your own country';
      case 'Instagram':
        return 'SIM card manipulation 1 and 2, proxy via wifi or ethernet';
      case 'Twitter':
        return 'any proxy';
      case 'Tinder and other dating apps':
        return 'GPS faker, SIM card manipulation 1 and 2, proxy via wifi or ethernet, sms verify';
      case 'Youtube Shorts':
        return 'proxy via wifi or ethernet';
      default:
        return 'proxy via wifi or ethernet';
    }
  };

  const getRelevantArticles = (platform, isBanned) => {
    const proxyLinks = [
      { title: 'Solution 1 - Share proxy via wifi from your PC', path: '/articles/ip/wifi-proxy.md' },
      { title: 'Solution 2 - Share proxy from PC via Ethernet', path: '/articles/ip/ethernet-proxy.md' },
      { title: 'Solution 3 - Router Proxy', path: '/articles/ip/router-proxy.md' }
    ];

    const bannedCountryLinks = [
      { title: 'SIM card manipulation 1', path: '/articles/sim-card/manipulation1.md' },
      { title: 'SIM card manipulation 2', path: '/articles/sim-card/manipulation2.md' },
      ...proxyLinks
    ];

    if (platform === 'TikTok') {
      if (!isBanned) {
        return {
          title: 'For TikTok, country not banned:',
          links: [
            { title: 'SIM card manipulation 1', path: '/articles/sim-card/manipulation1.md' },
            { title: 'SIM card manipulation 2', path: '/articles/sim-card/manipulation2.md' },
            { title: 'No SIM Card Approach?', path: '/articles/sim-card/nosimcard.md' },
            { title: 'Do you need to use geotargeted IP?', path: '/articles/ip/geotargeted-ip.md' },
            { title: 'Can 4G IP be banned?', path: '/articles/ip/4g-ip-ban.md' },
            { title: 'TikTok Solution - Local 4G', path: '/articles/ip/tiktok_solution.md' },
            { title: 'For TikTok? No problem', path: '/articles/tiktok/usa-targeting.md' }
          ],
          quote: "If you are not from banned country in TikTok, it's enough to only manipulate ICCID and IMSI. The 4G internet can be shared from second phone via wifi."
        };
      } else {
        return {
          title: 'For TikTok, country banned:',
          links: bannedCountryLinks,
          quote: "If you are from banned country in TikTok, you must to use ICCID and IMSI manipulation, but also provide proxy from PC via wifi or ethernet"
        };
      }
    }

    if (platform === 'Instagram') {
      return {
        title: 'For Instagram:',
        links: bannedCountryLinks,
        quote: "For Instagram it's required to make ICCID and IMSI manipulation, as well provide proxy from PC via wifi or ethernet"
      };
    }

    if (platform === 'Tinder and other dating apps') {
      return {
        title: 'For Tinder and dating apps:',
        links: [
          ...bannedCountryLinks,
          { title: 'GPS mockup Android', path: '/articles/gps/android-mockup.md' },
          { title: 'GPS mockup iOS', path: '/articles/gps/ios-mockup.md' }
        ],
        quote: "For Tinder it's required to make ICCID and IMSI manipulation, as well provide proxy from PC via wifi or ethernet. Also GPS mockup is required and SMS verification"
      };
    }

    if (platform === 'Twitter') {
      return {
        title: 'For Twitter:',
        links: proxyLinks,
        quote: "For Twitter is not required to do any proxy. Anyway you can try with proxy from PC. The best would be to use Graphene OS with 32 user profiles and 5 Youtube accounts each profile"
      };
    }

    if (platform === 'Youtube Shorts') {
      return {
        title: 'For Youtube Shorts:',
        links: [
          ...proxyLinks,
          { title: 'Sign up with Google & Apple', path: '/articles/email/google-signup.md' }
        ],
        quote: "For Youtube it's not required to do any proxy. Anyway you can try with proxy from PC. The best would be to use Graphene OS with 32 user profiles and 5 Youtube accounts each profile"
      };
    }

    return null;
  };

  const handlePromotionChange = (option) => {
    setPromotion(option);
    setMethod(getRecommendedMethod(selectedPlatform, banned));
    setArticles(getRelevantArticles(selectedPlatform, banned));
  };

  // Preserve state when navigating back
  useEffect(() => {
    const handlePopState = () => {
      if (step === 5) {
        window.history.pushState(null, '', window.location.pathname);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [step]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('questionnaire_platform', selectedPlatform);
    localStorage.setItem('questionnaire_step', step);
    localStorage.setItem('questionnaire_same', JSON.stringify(same));
    localStorage.setItem('questionnaire_banned', JSON.stringify(banned));
    localStorage.setItem('questionnaire_phone', phone);
    localStorage.setItem('questionnaire_promotion', promotion);
    localStorage.setItem('questionnaire_method', method);
    localStorage.setItem('questionnaire_articles', JSON.stringify(articles));
  }, [selectedPlatform, step, same, banned, phone, promotion, method, articles]);

  useEffect(() => {
    // Reset animation when step changes
    setIsVisible(false);
    setTimeout(() => setIsVisible(true), 100);
  }, [step]);

  // Add animation to each step render
  const renderWithAnimation = (content) => (
    <div className={`platform-selector fade-in-content ${isVisible ? 'visible' : ''}`}>
      {content}
    </div>
  );

  const handleBack = () => {
    setStep(1);
    setSelectedPlatform('');
    setSame(null);
    setBanned(null);
    setPhone(null);
    setPromotion(null);
    // Clear localStorage when starting over
    localStorage.removeItem('questionnaire_platform');
    localStorage.removeItem('questionnaire_step');
    localStorage.removeItem('questionnaire_same');
    localStorage.removeItem('questionnaire_banned');
    localStorage.removeItem('questionnaire_phone');
    localStorage.removeItem('questionnaire_promotion');
    localStorage.removeItem('questionnaire_method');
    localStorage.removeItem('questionnaire_articles');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 1 && selectedPlatform) {
      console.log('Step 1 - Selected Platform:', selectedPlatform);
      setStep(2);
    } else if (step === 2 && same !== null) {
      console.log('Step 2 - Same Country:', same);
      setStep(3);
    } else if (step === 3 && banned !== null) {
      console.log('Step 3 - Banned Country:', banned);
      setStep(4);
    } else if (step === 4 && phone !== null) {
      console.log('Step 4 - Phone Type:', phone);
      setStep(5);
    }
  };

  if (step === 5) {
    return renderWithAnimation(
      <div className="platform-selector">
        <form>
          <h2 className="text-xl font-semibold mb-6">What you want to promote?</h2>
          <div className="space-y-3">
            {[
              'Only Fans',
              'Adult CPA',
              'Shop',
              'Dropshipping',
              'Non Adult CPA',
              'Affiliate programs',
              'ETSY'
            ].map((option) => (
              <div key={option} className="flex items-center mb-4">
                <input
                  type="radio"
                  id={option}
                  name="promotion"
                  value={option}
                  checked={promotion === option}
                  onChange={() => handlePromotionChange(option)}
                  className="h-3 w-3 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor={option} className="ml-3 block text-xs font-medium">
                  {option}
                </label>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleBack}
            className="next-button"
          >
            Back
          </button>
        </form>
        <div className="selection-summary mt-6 text-gray-400 font-mono">
          {`> You have chosen: ${selectedPlatform}`}
          <div className="mt-4">
            {`> Your country is ${same ? 'same as' : 'different than'} targeted`}
          </div>
          <div className="mt-4">
            {`> You are ${banned ? '' : 'not '}from banned country`}
          </div>
          <div className="mt-4">
            {`> Your phone is: ${phone === 'pixel' ? 'Pixel with Graphene OS' : phone}`}
          </div>
          {promotion !== null && (
            <div className="mt-4">
              {`> You want to promote: ${promotion}`}
            </div>
          )}
        </div>
        {method !== null && (
          <div className="selection-summary mt-8 text-gray-400 font-mono">
            {`> Your best method is: ${method}`}
          </div>
        )}
        {articles !== null && (
          <div className="mt-8 text-white">
            <h3 className="text-lg font-semibold mb-4">{articles.title}</h3>
            <div className="text-sm opacity-90">Read articles:</div>
            <ul className="mt-4 space-y-3 text-[#94a3b8] list-disc pl-4">
              {articles.links.map((article, index) => (
                <li 
                  key={index}
                  className="cursor-pointer p-[2px] px-[10px] rounded border-b border-[rgba(255,255,255,0.05)] last:border-b-0 transition-[background-color] duration-200 ease-in-out hover:bg-[rgba(255,255,255,0.1)]"
                  onClick={() => onArticleSelect(article.path)}
                >
                  <div className="flex items-center gap-2">
                    <span className="book-icon opacity-80">📖</span>
                    <span>{article.title}</span>
                  </div>
                </li>
              ))}
            </ul>
            {articles.quote && (
              <div className="selection-summary mt-6 text-gray-400 font-mono">
                {`> ${articles.quote}`}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (step === 4) {
    return renderWithAnimation(
      <div className="platform-selector">
        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-6">Your phone is</h2>
          <div className="space-y-3">
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="iphone"
                name="phone"
                value="iphone"
                checked={phone === 'iphone'}
                onChange={() => setPhone('iphone')}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="iphone" className="ml-3 block text-xs font-medium">
                iPhone
              </label>
            </div>
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="android"
                name="phone"
                value="android"
                checked={phone === 'android'}
                onChange={() => setPhone('android')}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="android" className="ml-3 block text-xs font-medium">
                Android
              </label>
            </div>
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="pixel"
                name="phone"
                value="pixel"
                checked={phone === 'pixel'}
                onChange={() => setPhone('pixel')}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="pixel" className="ml-3 block text-xs font-medium">
                Pixel with Graphene OS
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={phone === null}
            className="next-button"
          >
            Next
          </button>
        </form>
        <div className="selection-summary mt-6 text-gray-400 font-mono">
          {`> You have chosen: ${selectedPlatform}`}
          <div className="mt-4">
            {`> Your country is ${same ? 'same as' : 'different than'} targeted`}
          </div>
          <div className="mt-4">
            {`> You are ${banned ? '' : 'not '}from banned country`}
          </div>
          {phone !== null && (
            <div className="mt-4">
              {`> Your phone is: ${phone === 'pixel' ? 'Pixel with Graphene OS' : phone}`}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 3) {
    return renderWithAnimation(
      <div className="platform-selector">
        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-6">Are you from banned country?</h2>
          <div className="space-y-3">
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="yes"
                name="banned"
                value="yes"
                checked={banned === true}
                onChange={() => setBanned(true)}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="yes" className="ml-3 block text-xs font-medium">
                Yes
              </label>
            </div>
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="no"
                name="banned"
                value="no"
                checked={banned === false}
                onChange={() => setBanned(false)}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="no" className="ml-3 block text-xs font-medium">
                No
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={banned === null}
            className="next-button"
          >
            Next
          </button>
        </form>
        <div className="selection-summary mt-6 text-gray-400 font-mono">
          {`> You have chosen: ${selectedPlatform}`}
          <div className="mt-4">
            {`> Your country is ${same ? 'same as' : 'different than'} targeted`}
          </div>
          {banned !== null && (
            <div className="mt-4">
              {`> You are ${banned ? '' : 'not '}from banned country`}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 2) {
    return renderWithAnimation(
      <div className="platform-selector">
        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-6">What is your country</h2>
          <div className="space-y-3">
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="same"
                name="country"
                value="same"
                checked={same === true}
                onChange={() => setSame(true)}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="same" className="ml-3 block text-xs font-medium">
                Same as targeted
              </label>
            </div>
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="different"
                name="country"
                value="different"
                checked={same === false}
                onChange={() => setSame(false)}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="different" className="ml-3 block text-xs font-medium">
                Different than targeted
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={same === null}
            className="next-button"
          >
            Next
          </button>
        </form>
        <div className="selection-summary mt-6 text-gray-400 font-mono">
          {`> You have chosen: ${selectedPlatform}`}
          {same !== null && (
            <div className="mt-4">
              {`> Your country is ${same ? 'same as' : 'different than'} targeted`}
            </div>
          )}
        </div>
      </div>
    );
  }

  return renderWithAnimation(
    <div className="platform-selector">
      <form onSubmit={handleSubmit}>
        <p className="text-sm text-gray-400 mb-6">Because many people can be overwhelmed by the amount of information, I've made an interactive questionnaire to help you choose the best method for your case.</p>
        <h2 className="text-xl font-semibold mb-6">What platform do you want to use?</h2>
        <div className="space-y-3">
          {platforms.map((platform) => (
            <div key={platform} className="flex items-center mb-4">
              <input
                type="radio"
                id={platform}
                name="platform"
                value={platform}
                checked={selectedPlatform === platform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="h-3 w-3 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor={platform} className="ml-3 block text-xs font-medium">
                {platform}
              </label>
            </div>
          ))}
        </div>
        <button
          type="submit"
          disabled={!selectedPlatform}
          className="next-button"
        >
          Next
        </button>
      </form>
    </div>
  );
};

export default PlatformSelector; 